import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Modal, Typography } from "antd";

import PaymentMethodSection, { mobileMoneyError }
    from "../../../ass_components/account/business/notifications/drawer/checkout/PaymentMethodSection";
import TransactionService from "../../../servics/notification/transaction/TransactionService";
import { payContract } from "../../../servics/marketplace/presaleContractApi";
import { apiErrorMessage, newIdempotencyKey } from "../../../constants/marketplaceCheckout";
import { ACTIVE_CONTRACT_PAYMENT, CONTRACT_PAYMENT_METHODS } from "../../../constants/presaleContract";
import { formatMoney } from "../../../utils/money";

const { Paragraph, Text } = Typography;

/** Catalogue du pays de l'acheteur réduit aux moyens en ligne acceptés pour le préfinancement. */
const onlineOnly = (catalog) => catalog && {
    ...catalog,
    methods: (catalog.methods || []).filter((method) => CONTRACT_PAYMENT_METHODS.includes(method.method)),
};

/**
 * Paiement de la somme attendue sur un contrat (avance à la signature, solde à la livraison) :
 * le montant est celui du contrat (amountDue), reçu par le séquestre ASSIGANTO. Carte : redirection
 * vers la page du prestataire ; Mobile Money : confirmation sur le téléphone, le suivi se fait dans
 * la fiche du contrat. Une même tentative garde sa clé d'idempotence (double clic, nouvel essai).
 */
const ContractPaymentModal = ({ open, country, contract, onClose, onStarted }) => {
    const [catalog, setCatalog] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogError, setCatalogError] = useState(null);
    const [method, setMethod] = useState(null);
    const [operator, setOperator] = useState(null);
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const keyRef = useRef(null);

    const currency = contract?.currency?.code;
    const kind = contract?.status === "AWAITING_ADVANCE" ? "ADVANCE" : "BALANCE";

    const loadCatalog = useCallback(async () => {
        setCatalogLoading(true);
        setCatalogError(null);
        try {
            setCatalog(onlineOnly(await TransactionService.getPaymentOptions(currency)));
        } catch (loadError) {
            setCatalogError(apiErrorMessage(loadError, "Impossible de charger les moyens de paiement."));
        } finally {
            setCatalogLoading(false);
        }
    }, [currency]);

    useEffect(() => {
        if (!open) return;
        keyRef.current = newIdempotencyKey();
        setMethod(null);
        setOperator(null);
        setPhone("");
        setError(null);
        loadCatalog();
    }, [open, loadCatalog]);

    const invalid = !method || (method === "MOBILE_MONEY"
        && Boolean(mobileMoneyError({ operator, phone, phoneRule: catalog?.phone })));

    const pay = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const mobile = method === "MOBILE_MONEY";
            const payment = await payContract(country, contract.id, {
                method,
                operator: mobile ? operator : null,
                phoneNumber: mobile ? `${catalog?.phone?.dialCode ?? ""}${phone.replace(/[\s.-]/g, "")}` : null,
            }, keyRef.current);
            if (payment?.nextActionType === "REDIRECT" && payment.nextActionUrl
                && ACTIVE_CONTRACT_PAYMENT.includes(payment.status)) {
                window.location.assign(payment.nextActionUrl);
                return;
            }
            onStarted?.(payment);
        } catch (payError) {
            setError(apiErrorMessage(payError, "Le paiement n'a pas pu être lancé. Réessayez dans un instant."));
            // Refus définitif : un nouvel essai part avec une nouvelle clé.
            keyRef.current = newIdempotencyKey();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            title={`Payer ${kind === "ADVANCE" ? "l'avance" : "le solde"} — contrat ${contract?.reference ?? ""}`}
            okText="Payer"
            cancelText="Fermer"
            okButtonProps={{ disabled: invalid || catalog?.paymentServiceAvailable === false }}
            confirmLoading={submitting}
            onOk={pay}
            onCancel={onClose}
            destroyOnHidden
        >
            {contract && (
                <Paragraph>
                    Montant : <Text strong>{formatMoney(contract.amountDue, currency)}</Text>.{" "}
                    {kind === "ADVANCE"
                        ? "Reçue par le séquestre ASSIGANTO, l'avance est versée au producteur pour lancer la production."
                        : "Reçu par le séquestre ASSIGANTO, le solde est versé au producteur, commission déduite de son côté."}
                </Paragraph>
            )}
            {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}
            <PaymentMethodSection
                title="Moyen de paiement"
                catalog={catalog}
                catalogLoading={catalogLoading}
                catalogError={catalogError}
                paymentMethod={method}
                onPaymentMethodChange={setMethod}
                operator={operator}
                onOperatorChange={setOperator}
                phone={phone}
                onPhoneChange={setPhone}
                onRetryCatalog={loadCatalog}
                disabled={submitting}
            />
        </Modal>
    );
};

export default ContractPaymentModal;
